import React, { useState } from 'react';
import { Check, ShieldCheck, Truck, FileText, Plus } from 'lucide-react';
import { SalesNotification } from './SalesNotification';
import { UrgencyBar } from './UrgencyBar';
import { useCurrency } from '../hooks/useCurrency';
export function CheckoutPage() {
  const {
    formatPrice,
    isLoading,
    debugInfo,
    currencyCode,
    exchangeRate,
    setManualCurrency,
    availableCurrencies
  } = useCurrency();
  const [selectedBundle, setSelectedBundle] = useState<'single' | 'double' | 'triple'>('triple');
  const [shippingProtection, setShippingProtection] = useState(false);
  // Countdown timer state - 15 minutes
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes in seconds
  // Countdown effect
  useState(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 15 * 60; // Reset to 15 minutes
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  });
  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const bundles = {
    single: {
      name: '1X KNOVA™ Automatic Peeler Machine',
      quantity: 1,
      price: 39.97,
      originalPrice: 79.94,
      discount: '50% OFF',
      badge: 'Hot Sale',
      image: 'https://cdn.magicpatterns.com/uploads/vwV9Abjn4H6p4eTgHU11sv/image.png',
      shopifyId: '48074075078875',
      freeShipping: true
    },
    double: {
      name: '2X KNOVA™ Automatic Peeler Machine',
      quantity: 2,
      price: 79.94,
      originalPrice: 159.88,
      discount: '54% OFF',
      badge: 'Best Value',
      image: 'https://cdn.magicpatterns.com/uploads/vwV9Abjn4H6p4eTgHU11sv/image.png',
      shopifyId: '48074075111643',
      freeShipping: true
    },
    triple: {
      name: '3X KNOVA™ Automatic Peeler Machine',
      quantity: 3,
      price: 119.91,
      originalPrice: 239.82,
      discount: '58% OFF',
      badge: 'Most Popular',
      image: 'https://cdn.magicpatterns.com/uploads/vwV9Abjn4H6p4eTgHU11sv/image.png',
      shopifyId: '48074075144411',
      freeShipping: true
    }
  };
  const calculateTotal = () => {
    let total = bundles[selectedBundle].price;
    if (shippingProtection) total += 1.99;
    return total; // Return number instead of string
  };
  const handleCheckout = () => {
    const bundle = bundles[selectedBundle];
    const cartUrl = `https://knovastore.com/cart/${bundle.shopifyId}:${bundle.quantity}`;
    window.location.href = cartUrl;
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-44">
      {/* Currency Selector Bar */}
      <div className="bg-gray-100 border-b border-gray-200 py-1.5 px-4 flex items-center justify-end gap-2" data-id="element-45">
        <span className="text-xs text-gray-500" data-id="element-46">Currency:</span>
        <select value={currencyCode} onChange={e => setManualCurrency(e.target.value)} className="text-sm font-semibold bg-white border border-gray-300 rounded px-2 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-900" data-id="element-47">
          {availableCurrencies.map(c => <option key={c.code} value={c.code} data-id="element-48">
              {c.label}
            </option>)}
        </select>
      </div>

      {/* Sales Notification Popup */}
      <SalesNotification data-id="element-49" />

      {/* Hero Banner */}
      <div className="w-full" data-id="element-50">
        {/* Mobile Banner */}
        <img src="https://cdn.magicpatterns.com/uploads/xnLsDGgQc8uLaRonKRTo9M/770c253c-4c74-49d8-ac8e-f1618476cd2d.png" alt="Product banner" className="w-full h-auto object-contain md:hidden" loading="eager" fetchpriority="high" data-id="element-51" />
        {/* Desktop Banner */}
        <img src="https://cdn.magicpatterns.com/uploads/eDCNVBNpYYPFpBxSfNBy7e/200648a7-8eb7-458e-ab86-715a0a85e752.png" alt="Product banner" className="hidden md:block w-full h-auto object-cover" loading="eager" fetchpriority="high" data-id="element-52" />
      </div>

      <UrgencyBar data-id="element-53" />

      <div className="max-w-6xl mx-auto px-4 py-12" data-id="element-54">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-55">
          {/* Left Column - Bundle Selection */}
          <div id="pricing-section" data-id="element-56">
            <h2 className="text-2xl font-bold mb-6" data-id="element-57">
              Step 1: Bundle Of Choice
            </h2>

            {/* Bundle Options */}
            <div className="space-y-4 max-w-2xl md:max-w-none mx-auto md:mx-0" data-id="element-58">
              {/* Single Bundle */}
              <div onClick={() => setSelectedBundle('single')} className={`rounded-lg border-2 p-4 md:p-4 cursor-pointer transition-all ${selectedBundle === 'single' ? 'bg-green-50 border-blue-900 shadow-lg' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-59">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4" data-id="element-60">
                  {/* Radio Button and Image Row */}
                  <div className="flex items-center gap-3 md:gap-3 w-full md:w-auto" data-id="element-61">
                    {/* Radio Button */}
                    <div className="flex-shrink-0" data-id="element-62">
                      <div className={`w-6 h-6 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center ${selectedBundle === 'single' ? 'border-blue-900 bg-blue-900' : 'border-gray-300'}`} data-id="element-63">
                        {selectedBundle === 'single' && <div className="w-3 h-3 md:w-3 md:h-3 bg-white rounded-full" data-id="element-64"></div>}
                      </div>
                    </div>

                    {/* Product Image */}
                    <img src={bundles.single.image} alt={bundles.single.name} className="w-20 h-20 md:w-20 md:h-20 object-cover rounded flex-shrink-0" data-id="element-65" />

                    {/* Product Info - Mobile */}
                    <div className="flex-1 md:hidden min-w-0" data-id="element-66">
                      <h3 className="font-bold text-base leading-tight mb-1 line-clamp-2" data-id="element-67">
                        {bundles.single.name}
                      </h3>
                      <div className="flex items-center gap-1 text-blue-900 text-xs font-semibold" data-id="element-68">
                        <Truck className="w-4 h-4" data-id="element-69" />
                        <span data-id="element-70">Free Shipping</span>
                      </div>
                    </div>
                  </div>

                  {/* Product Info - Desktop */}
                  <div className="hidden md:block flex-1 min-w-0" data-id="element-71">
                    <h3 className="font-bold text-base mb-1" data-id="element-72">
                      {bundles.single.name}
                    </h3>
                    <div className="flex items-center gap-1 text-blue-900 text-sm font-semibold" data-id="element-73">
                      <Truck className="w-4 h-4" data-id="element-74" />
                      <span data-id="element-75">Free Shipping</span>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="flex items-center justify-between w-full md:w-auto md:block md:text-right flex-shrink-0 mt-2 md:mt-0" data-id="element-76">
                    <div className="bg-yellow-400 text-black text-xs md:text-xs font-bold px-2.5 py-1 md:py-1 rounded inline-block mb-0 md:mb-1" data-id="element-77">
                      {bundles.single.discount}
                    </div>
                    <div className="text-right" data-id="element-78">
                      <div className="text-red-600 line-through text-sm md:text-sm font-bold" data-id="element-79">
                        {isLoading ? '...' : formatPrice(bundles.single.originalPrice)}
                      </div>
                      <div className="text-2xl md:text-2xl font-bold text-black" data-id="element-80">
                        {isLoading ? '...' : formatPrice(bundles.single.price)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Double Bundle */}
              <div onClick={() => setSelectedBundle('double')} className={`rounded-lg border-2 p-4 md:p-4 cursor-pointer transition-all relative ${selectedBundle === 'double' ? 'bg-green-50 border-blue-900 shadow-lg' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-81">
                {/* Best Value Badge */}
                <div className="absolute -top-3 right-4 bg-blue-900 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1" data-id="element-82">
                  ✓ {bundles.double.badge}
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4" data-id="element-83">
                  {/* Radio Button and Images Row */}
                  <div className="flex items-center gap-3 md:gap-3 w-full md:w-auto" data-id="element-84">
                    {/* Radio Button */}
                    <div className="flex-shrink-0" data-id="element-85">
                      <div className={`w-6 h-6 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center ${selectedBundle === 'double' ? 'border-blue-900 bg-blue-900' : 'border-gray-300'}`} data-id="element-86">
                        {selectedBundle === 'double' && <div className="w-3 h-3 md:w-3 md:h-3 bg-white rounded-full" data-id="element-87"></div>}
                      </div>
                    </div>

                    {/* Product Images */}
                    <div className="flex gap-2 md:gap-2 w-20 md:w-20" data-id="element-88">
                      <img src={bundles.double.image} alt={bundles.double.name} className="w-9 h-20 md:w-9 md:h-20 object-cover rounded flex-shrink-0" data-id="element-89" />
                      <img src={bundles.double.image} alt={bundles.double.name} className="w-9 h-20 md:w-9 md:h-20 object-cover rounded flex-shrink-0" data-id="element-90" />
                    </div>

                    {/* Product Info - Mobile */}
                    <div className="flex-1 md:hidden min-w-0" data-id="element-91">
                      <h3 className="font-bold text-base leading-tight mb-1 line-clamp-2" data-id="element-92">
                        {bundles.double.name}
                      </h3>
                      <div className="flex items-center gap-1 text-blue-900 text-xs font-semibold" data-id="element-93">
                        <Truck className="w-4 h-4" data-id="element-94" />
                        <span data-id="element-95">Free Shipping</span>
                      </div>
                    </div>
                  </div>

                  {/* Product Info - Desktop */}
                  <div className="hidden md:block flex-1 min-w-0" data-id="element-96">
                    <h3 className="font-bold text-base mb-1" data-id="element-97">
                      {bundles.double.name}
                    </h3>
                    <div className="flex items-center gap-1 text-blue-900 text-sm font-semibold" data-id="element-98">
                      <Truck className="w-4 h-4" data-id="element-99" />
                      <span data-id="element-100">Free Shipping</span>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="flex items-center justify-between w-full md:w-auto md:block md:text-right flex-shrink-0 mt-2 md:mt-0" data-id="element-101">
                    <div className="bg-yellow-400 text-black text-xs md:text-xs font-bold px-2.5 py-1 md:py-1 rounded inline-block mb-0 md:mb-1" data-id="element-102">
                      {bundles.double.discount}
                    </div>
                    <div className="text-right" data-id="element-103">
                      <div className="text-red-600 line-through text-sm md:text-sm font-bold" data-id="element-104">
                        {isLoading ? '...' : formatPrice(bundles.double.originalPrice)}
                      </div>
                      <div className="text-2xl md:text-2xl font-bold text-black" data-id="element-105">
                        {isLoading ? '...' : formatPrice(bundles.double.price)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Triple Bundle */}
              <div onClick={() => setSelectedBundle('triple')} className={`rounded-lg border-2 p-4 md:p-4 cursor-pointer transition-all relative ${selectedBundle === 'triple' ? 'bg-green-50 border-blue-900 shadow-lg' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-106">
                {/* Most Popular Badge */}
                <div className="absolute -top-3 right-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1" data-id="element-107">
                  🔥 {bundles.triple.badge}
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4" data-id="element-108">
                  {/* Radio Button and Images Row */}
                  <div className="flex items-center gap-3 md:gap-3 w-full md:w-auto" data-id="element-109">
                    {/* Radio Button */}
                    <div className="flex-shrink-0" data-id="element-110">
                      <div className={`w-6 h-6 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center ${selectedBundle === 'triple' ? 'border-blue-900 bg-blue-900' : 'border-gray-300'}`} data-id="element-111">
                        {selectedBundle === 'triple' && <div className="w-3 h-3 md:w-3 md:h-3 bg-white rounded-full" data-id="element-112"></div>}
                      </div>
                    </div>

                    {/* Product Images */}
                    <div className="flex gap-1 md:gap-1 w-20 md:w-20" data-id="element-113">
                      <img src={bundles.triple.image} alt={bundles.triple.name} className="w-6 h-20 md:w-6 md:h-20 object-cover rounded flex-shrink-0" data-id="element-114" />
                      <img src={bundles.triple.image} alt={bundles.triple.name} className="w-6 h-20 md:w-6 md:h-20 object-cover rounded flex-shrink-0" data-id="element-115" />
                      <img src={bundles.triple.image} alt={bundles.triple.name} className="w-6 h-20 md:w-6 md:h-20 object-cover rounded flex-shrink-0" data-id="element-116" />
                    </div>

                    {/* Product Info - Mobile */}
                    <div className="flex-1 md:hidden min-w-0" data-id="element-117">
                      <h3 className="font-bold text-base leading-tight mb-1 line-clamp-2" data-id="element-118">
                        {bundles.triple.name}
                      </h3>
                      <div className="flex items-center gap-1.5 text-green-600 text-base font-bold mb-1" data-id="element-119">
                        <Plus className="w-5 h-5" strokeWidth={3} data-id="element-120" />
                        <img src="https://cdn.magicpatterns.com/uploads/ss4LTFmXaHNLR9kCEtoc6j/image.png" alt="PDF" className="w-6 h-6" data-id="element-121" />
                        <span data-id="element-122">KNOVA™ Recipe eBook - FREE</span>
                      </div>
                      <div className="flex items-center gap-1 text-blue-900 text-xs font-semibold" data-id="element-123">
                        <Truck className="w-4 h-4" data-id="element-124" />
                        <span data-id="element-125">Free Shipping</span>
                      </div>
                    </div>
                  </div>

                  {/* Product Info - Desktop */}
                  <div className="hidden md:block flex-1 min-w-0" data-id="element-126">
                    <h3 className="font-bold text-base mb-1" data-id="element-127">
                      {bundles.triple.name}
                    </h3>
                    <div className="flex items-center gap-2 text-green-600 text-lg font-bold mb-1" data-id="element-128">
                      <Plus className="w-6 h-6" strokeWidth={3} data-id="element-129" />
                      <img src="https://cdn.magicpatterns.com/uploads/ss4LTFmXaHNLR9kCEtoc6j/image.png" alt="PDF" className="w-7 h-7" data-id="element-130" />
                      <span data-id="element-131">KNOVA™ Recipe eBook - FREE</span>
                    </div>
                    <div className="flex items-center gap-1 text-blue-900 text-sm font-semibold" data-id="element-132">
                      <Truck className="w-4 h-4" data-id="element-133" />
                      <span data-id="element-134">Free Shipping</span>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="flex items-center justify-between w-full md:w-auto md:block md:text-right flex-shrink-0 mt-2 md:mt-0" data-id="element-135">
                    <div className="bg-yellow-400 text-black text-xs md:text-xs font-bold px-2.5 py-1 md:py-1 rounded inline-block mb-0 md:mb-1" data-id="element-136">
                      {bundles.triple.discount}
                    </div>
                    <div className="text-right" data-id="element-137">
                      <div className="text-red-600 line-through text-sm md:text-sm font-bold" data-id="element-138">
                        {isLoading ? '...' : formatPrice(bundles.triple.originalPrice)}
                      </div>
                      <div className="text-2xl md:text-2xl font-bold text-black" data-id="element-139">
                        {isLoading ? '...' : formatPrice(bundles.triple.price)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Protection */}
            <div className="flex items-center gap-2 mt-6 mb-6" data-id="element-140">
              <input type="checkbox" checked={shippingProtection} onChange={e => setShippingProtection(e.target.checked)} className="w-5 h-5 accent-green-600" data-id="element-141" />
              <span className="text-sm" data-id="element-142">
                Shipping Protection {isLoading ? '...' : formatPrice(1.99)}
              </span>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6" data-id="element-143">
              <h3 className="text-xl font-bold mb-4" data-id="element-144">Order Summary</h3>

              <div className="space-y-3" data-id="element-145">
                {/* Selected Bundle */}
                <div className="flex justify-between items-center" data-id="element-146">
                  <span className="text-gray-700" data-id="element-147">
                    {bundles[selectedBundle].name}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-148">
                    <span className="font-semibold" data-id="element-149">
                      {isLoading ? '...' : formatPrice(bundles[selectedBundle].price)}
                    </span>
                    <span className="text-gray-400 line-through text-sm" data-id="element-150">
                      {isLoading ? '...' : formatPrice(bundles[selectedBundle].originalPrice)}
                    </span>
                  </div>
                </div>

                {/* Shipping */}
                <div className="flex justify-between items-center pt-3 border-t" data-id="element-151">
                  <span className="text-gray-700" data-id="element-152">Shipping</span>
                  <span className="font-semibold text-green-600" data-id="element-153">
                    {bundles[selectedBundle].freeShipping ? 'FREE SHIPPING' : isLoading ? '...' : formatPrice(9.99)}
                  </span>
                </div>

                {/* Shipping Protection */}
                {shippingProtection && <div className="flex justify-between items-center" data-id="element-154">
                    <span className="text-gray-700" data-id="element-155">Shipping Protection</span>
                    <span className="font-semibold" data-id="element-156">
                      {isLoading ? '...' : formatPrice(1.99)}
                    </span>
                  </div>}

                {/* Total */}
                <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300" data-id="element-157">
                  <span className="text-xl font-bold" data-id="element-158">Total</span>
                  <span className="text-2xl font-bold text-blue-900" data-id="element-159">
                    {isLoading ? '...' : formatPrice(calculateTotal())}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Payment */}
          <div data-id="element-160">
            <h2 className="text-2xl font-bold mb-4" data-id="element-161">
              Step 2: Payment Information
            </h2>

            <div className="bg-white rounded-lg p-6 shadow-lg" data-id="element-162">
              {/* Checkout Button */}
              <button onClick={handleCheckout} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-lg mb-6 text-lg" data-id="element-163">
                Checkout - {isLoading ? '...' : formatPrice(calculateTotal())}
              </button>

              {/* Trustpilot Rating */}
              <div className="flex justify-center mb-6" data-id="element-164">
                <img src="https://cdn.magicpatterns.com/uploads/kYRB526XDa6ASxedvMc1hC/image.png" alt="Trustpilot rating - Excellent 4.1 out of 5" className="max-w-[320px] md:max-w-sm h-auto" data-id="element-165" />
              </div>

              {/* Security Section */}
              <div className="border-t pt-4" data-id="element-166">
                <div className="text-center font-bold mb-3 flex items-center justify-center gap-2" data-id="element-167">
                  <ShieldCheck className="w-5 h-5 text-green-600" data-id="element-168" />
                  SECURE PAYMENT GUARANTEED
                </div>
                <p className="text-sm font-semibold mb-3" data-id="element-169">
                  Pay confidently with our secure methods
                </p>
                <div className="space-y-2 text-sm text-gray-700 mb-6" data-id="element-170">
                  <div className="flex items-start gap-2" data-id="element-171">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-172" />
                    <span data-id="element-173">Card information is secure and uncompromised</span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-174">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-175" />
                    <span data-id="element-176">
                      Knova follows the Payment Card Industry Data Security
                      Standard (PCI DSS) and other security standards when
                      handling card data
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-177">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-178" />
                    <span data-id="element-179">All data is encrypted</span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-180">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-181" />
                    <span data-id="element-182">Knova never sells your card information</span>
                  </div>
                </div>

                {/* Payment Icons */}
                <div className="border-t pt-4" data-id="element-183">
                  <img src="https://cdn.magicpatterns.com/uploads/tk2mH4rjPEA1miip6sME2b/image.png" alt="Payment methods and security badges" className="w-full h-auto" data-id="element-184" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Reviews Section */}
        <div className="mt-12 mb-8" data-id="element-185">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-186">
            {/* Review 1 */}
            <div className="bg-white rounded-lg p-6 shadow-md" data-id="element-187">
              <div className="flex items-center gap-3 mb-3" data-id="element-188">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center" data-id="element-189">
                  <Check className="w-6 h-6 text-white" data-id="element-190" />
                </div>
                <div data-id="element-191">
                  <h4 className="font-bold text-lg" data-id="element-192">Sarah M.</h4>
                  <div className="flex gap-1" data-id="element-193">
                    {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-xl" data-id="element-194">
                        ★
                      </span>)}
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm" data-id="element-195">
                I use it every day for my kids' apple snacks. It's so fast and
                easy—absolutely a must-have kitchen gadget.
              </p>
            </div>

            {/* Review 2 */}
            <div className="bg-white rounded-lg p-6 shadow-md" data-id="element-196">
              <div className="flex items-center gap-3 mb-3" data-id="element-197">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center" data-id="element-198">
                  <Check className="w-6 h-6 text-white" data-id="element-199" />
                </div>
                <div data-id="element-200">
                  <h4 className="font-bold text-lg" data-id="element-201">Michael T.</h4>
                  <div className="flex gap-1" data-id="element-202">
                    {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-xl" data-id="element-203">
                        ★
                      </span>)}
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm" data-id="element-204">
                Peeling potatoes for dinner used to take forever. Now it's done
                in minutes. My wife and I both love it.
              </p>
            </div>

            {/* Review 3 */}
            <div className="bg-white rounded-lg p-6 shadow-md" data-id="element-205">
              <div className="flex items-center gap-3 mb-3" data-id="element-206">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center" data-id="element-207">
                  <Check className="w-6 h-6 text-white" data-id="element-208" />
                </div>
                <div data-id="element-209">
                  <h4 className="font-bold text-lg" data-id="element-210">Jennifer K.</h4>
                  <div className="flex gap-1" data-id="element-211">
                    {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-xl" data-id="element-212">
                        ★
                      </span>)}
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm" data-id="element-213">
                Bought it for my mom, and she says it's her favorite tool now.
                She can prepare fruits without any hassle.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}